/** tm/legal/CompanyPicker.tsx — 업체 검색 선택 (이름·대표·사업자번호, 한글 IME 안전) */
import { useMemo, useRef, useState, useEffect } from 'react';
import { C, T, R, IMEText } from '../components/ui';
import { inputStyle, MUTED } from './legalTheme';

export interface PickCompany { id: string; name: string; ceo?: string; biz_no?: string }
interface Props { companies: PickCompany[]; exclude: string[]; disabled?: boolean; onPick: (c: PickCompany) => void }

const norm = (s: string) => s.replace(/[\s()㈜주식회사-]/g, '').toLowerCase();

export default function CompanyPicker({ companies, exclude, disabled, onPick }: Props) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const wrap = useRef<HTMLDivElement>(null);
  const hits = useMemo(() => {
    const k = norm(q);
    const ex = new Set(exclude);
    return companies.filter(c => !ex.has(c.id) && (!k || norm(c.name).includes(k) || norm(c.ceo || '').includes(k) || (c.biz_no || '').replace(/-/g, '').includes(k))).slice(0, 8);
  }, [q, companies, exclude]);
  useEffect(() => { setHi(0); }, [q]);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (!wrap.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const pick = (c: PickCompany) => { onPick(c); setQ(''); setOpen(false); };

  return (
    <div ref={wrap} style={{ position: 'relative', flex: 1, minWidth: 180, maxWidth: 320 }}>
      <IMEText value={q} disabled={disabled} className="lw-f" aria-label="업체 검색" role="combobox" aria-expanded={open}
        placeholder="업체 이름·대표자로 검색" onFocus={() => setOpen(true)}
        onChange={v => { setQ(v); setOpen(true); }}
        onKeyDown={e => {
          if (e.nativeEvent.isComposing) return;
          if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => Math.min(h + 1, hits.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(h - 1, 0)); }
          else if (e.key === 'Enter' && hits[hi]) { e.preventDefault(); pick(hits[hi]); }
          else if (e.key === 'Escape') setOpen(false);
        }}
        style={{ ...inputStyle, padding: '6px 10px' }} />
      {open && (
        <div role="listbox" style={{ position: 'absolute', left: 0, right: 0, top: 'calc(100% + 4px)', background: C.white, border: `1px solid ${C.border}`, borderRadius: R.sm, boxShadow: '0 8px 20px rgba(17,24,39,.12)', zIndex: 20, maxHeight: 260, overflowY: 'auto' }}>
          {hits.length === 0 && <div style={{ padding: '10px 12px', fontSize: T.sm, color: MUTED }}>{companies.length ? '맞는 업체가 없습니다' : '등록된 업체가 없습니다'}</div>}
          {hits.map((c, i) => (
            <div key={c.id} role="option" aria-selected={i === hi} onMouseDown={e => { e.preventDefault(); pick(c); }} onMouseEnter={() => setHi(i)} style={{
              padding: '7px 12px', cursor: 'pointer', fontSize: T.sm, background: i === hi ? C.accentLt : 'transparent', display: 'flex', gap: 8, alignItems: 'baseline',
            }}>
              <span style={{ fontWeight: 600, color: C.text }}>{c.name}</span>
              {c.ceo && <span style={{ fontSize: T.sm, color: MUTED }}>{c.ceo}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
